// @intent: Pure runtime state reducer for iframe commands from the editor parent
// @flow: RuntimeApp validates ParentToRuntimeMessage -> runtimeReducer -> RuntimeCanvas render props
// @stale: when protocol commands gain side effects beyond local render state

import type { Project } from '@mintyads/playable-api';
import type { ParentToRuntimeMessage, RuntimeMode } from '@mintyads/shared-types';

export interface RuntimeState {
  readonly project: Project | null;
  readonly mode: RuntimeMode;
  readonly viewport: { readonly width: number; readonly height: number };
  readonly highlightedLayerIds: readonly string[];
  readonly lastPatchRequestId: string | null;
  readonly restartNonce: number;
}

export const initialRuntimeState: RuntimeState = {
  project: null,
  mode: 'edit',
  viewport: { width: 320, height: 568 },
  highlightedLayerIds: [],
  lastPatchRequestId: null,
  restartNonce: 0,
};

export function runtimeReducer(state: RuntimeState, message: ParentToRuntimeMessage): RuntimeState {
  switch (message.type) {
    case 'runtime.init':
      return {
        ...state,
        project: message.payload.project,
        mode: message.payload.mode,
        viewport: message.payload.viewport,
        highlightedLayerIds: message.payload.highlightedLayerIds,
      };
    case 'runtime.setProject':
      return { ...state, project: message.payload.project };
    case 'runtime.applyPatch':
      return {
        ...state,
        project: message.payload.project,
        lastPatchRequestId: message.requestId ?? null,
      };
    case 'runtime.setMode':
      return { ...state, mode: message.payload.mode };
    case 'runtime.highlightLayers':
      return { ...state, highlightedLayerIds: message.payload.ids };
    case 'runtime.clearHighlights':
      return { ...state, highlightedLayerIds: [] };
    case 'runtime.restart':
      return {
        ...state,
        project: message.payload.project,
        restartNonce: state.restartNonce + 1,
      };
    case 'runtime.ping':
      return state;
    case 'runtime.queryState':
      return state;
    // §10 verifier commands are SIDE EFFECTS (dispatch real pointer events / read window.__match3 + Pixi bounds) handled
    // in RuntimeApp, not local render state — the reducer leaves state unchanged.
    case 'runtime.driveInput':
      return state;
    case 'runtime.queryMatch3':
      return state;
    // Runtime probe commands are also side effects: RuntimeApp samples collector state or drives real input,
    // while render state remains unchanged.
    case 'runtime.probeSnapshot':
      return state;
    case 'runtime.probeStartTrace':
      return state;
    case 'runtime.probeStopTrace':
      return state;
    case 'runtime.probeDriveAction':
      return state;
  }
}
